import { EVM_CONFIG, getActiveBaseShieldedPoolAddress } from "../../config";
import type { BaseHistoryEntry, BaseStoredNote } from "../types";

const poolAddress = getActiveBaseShieldedPoolAddress() ?? "";
const cacheNamespace = `${EVM_CONFIG.CHAIN_ID}_${EVM_CONFIG.SETTLEMENT_STACK}_${poolAddress.toLowerCase()}`;
const notesKeyPrefix = `base_app_notes_${cacheNamespace}_`;
const encryptedNotesKeyPrefix = `base_app_notes_encrypted_v1_${cacheNamespace}_`;
const historyKeyPrefix = `base_app_history_${cacheNamespace}_`;
const encryptedHistoryKeyPrefix = `base_app_history_encrypted_v1_${cacheNamespace}_`;
const bigintPrefix = "__stealthos_bigint:";
type JsonReviver = (this: unknown, key: string, value: unknown) => unknown;
type JsonReplacer = (this: unknown, key: string, value: unknown) => unknown;

function encodeBase64(bytes: Uint8Array): string {
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

function decodeBase64(value: string): ArrayBuffer {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
}

function noteJsonReplacer(_key: string, value: unknown): unknown {
  return typeof value === "bigint"
    ? `${bigintPrefix}${value.toString()}`
    : value;
}

function noteJsonReviver(_key: string, value: unknown): unknown {
  if (typeof value === "string" && value.startsWith(bigintPrefix)) {
    return BigInt(value.slice(bigintPrefix.length));
  }
  return value;
}

async function deriveEncryptedCacheKey(
  scope: string,
  address: string,
  cacheSecret: string,
): Promise<CryptoKey | null> {
  if (
    typeof window === "undefined" ||
    !window.crypto?.subtle ||
    !address ||
    !cacheSecret
  ) {
    return null;
  }
  const material = new TextEncoder().encode(
    [scope, cacheNamespace, address.trim().toLowerCase(), cacheSecret].join(
      ":",
    ),
  );
  const digest = await window.crypto.subtle.digest("SHA-256", material);
  return window.crypto.subtle.importKey(
    "raw",
    digest,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"],
  );
}

export async function loadEncryptedJsonCache<T>(
  scope: string,
  storageKey: string,
  address: string,
  cacheSecret: string,
  reviver?: JsonReviver,
): Promise<T | null> {
  const key = await deriveEncryptedCacheKey(scope, address, cacheSecret);
  if (!key) return null;
  const raw = localStorage.getItem(storageKey);
  if (!raw) return null;
  const payload = JSON.parse(raw) as { iv?: string; ciphertext?: string };
  if (!payload.iv || !payload.ciphertext) return null;
  const plaintext = await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv: decodeBase64(payload.iv) },
    key,
    decodeBase64(payload.ciphertext),
  );
  const text = new TextDecoder().decode(plaintext);
  return JSON.parse(text, reviver) as T;
}

async function preserveEncryptedCache(
  storageKey: string,
  ciphertext: string,
): Promise<void> {
  const digest = new Uint8Array(
    await window.crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(ciphertext),
    ),
  );
  const digestHex = Array.from(digest, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  const preservedKey = `${storageKey}:preserved:${digestHex}`;
  const preserved = localStorage.getItem(preservedKey);
  if (preserved !== null && preserved !== ciphertext) {
    throw new Error("Encrypted cache preservation conflict");
  }
  if (preserved === null) localStorage.setItem(preservedKey, ciphertext);
  if (localStorage.getItem(preservedKey) !== ciphertext) {
    throw new Error("Encrypted cache preservation did not persist");
  }
}

export async function preserveBaseWalletCaches(address: string): Promise<void> {
  const snapshots = [
    encryptedNotesKeyPrefix + address.toLowerCase(),
    encryptedHistoryKeyPrefix + address.toLowerCase(),
  ].map((storageKey) => ({
    storageKey,
    ciphertext: localStorage.getItem(storageKey),
  }));
  // A phrase can change the private identity without changing the public
  // wallet's signature. Preserve both caches without attempting decryption.
  for (const snapshot of snapshots) {
    if (snapshot.ciphertext !== null) {
      await preserveEncryptedCache(snapshot.storageKey, snapshot.ciphertext);
    }
  }
  for (const snapshot of snapshots) {
    if (localStorage.getItem(snapshot.storageKey) !== snapshot.ciphertext) {
      throw new Error("Encrypted cache changed during preservation");
    }
  }
}

export async function saveEncryptedJsonCache(
  scope: string,
  storageKey: string,
  address: string,
  value: unknown,
  cacheSecret: string,
  replacer?: JsonReplacer,
): Promise<void> {
  const key = await deriveEncryptedCacheKey(scope, address, cacheSecret);
  if (!key) return;
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(JSON.stringify(value, replacer));
  const ciphertext = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    encoded,
  );
  const previous = localStorage.getItem(storageKey);
  if (previous !== null) {
    let canOpenPrevious = false;
    try {
      const payload = JSON.parse(previous) as {
        iv: string;
        ciphertext: string;
      };
      await window.crypto.subtle.decrypt(
        { name: "AES-GCM", iv: decodeBase64(payload.iv) },
        key,
        decodeBase64(payload.ciphertext),
      );
      canOpenPrevious = true;
    } catch {
      // A restored private key can use a different wallet setup signature.
      // Retain the original encrypted cache before replacing its local slot.
    }
    if (!canOpenPrevious) {
      await preserveEncryptedCache(storageKey, previous);
    }
    if (localStorage.getItem(storageKey) !== previous) {
      throw new Error("Encrypted cache changed during preservation");
    }
  }
  localStorage.setItem(
    storageKey,
    JSON.stringify({
      iv: encodeBase64(iv),
      ciphertext: encodeBase64(new Uint8Array(ciphertext)),
      updatedAt: Date.now(),
    }),
  );
}

function parseHistoryEntries(value: unknown): BaseHistoryEntry[] {
  return Array.isArray(value) ? (value as BaseHistoryEntry[]) : [];
}

function historyEntryKey(entry: BaseHistoryEntry): string {
  return (
    entry.eventId ||
    `${(entry.txHash || "").toLowerCase()}|${entry.type}|${entry.amount}|${entry.symbol}`
  );
}

function mergeHistoryEntries(
  encrypted: BaseHistoryEntry[],
  legacy: BaseHistoryEntry[],
): BaseHistoryEntry[] {
  const byKey = new Map<string, BaseHistoryEntry>();
  for (const entry of [...encrypted, ...legacy]) {
    byKey.set(historyEntryKey(entry), entry);
  }
  return Array.from(byKey.values())
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, 100);
}

export function loadBaseNotes(address: string): BaseStoredNote[] {
  // Note persistence in localStorage is intentionally disabled to avoid storing
  // privacy-sensitive note secrets (npk/random/value/tokenHash) in plaintext.
  // Source of truth is remote note sync.
  try {
    localStorage.removeItem(notesKeyPrefix + address.toLowerCase());
  } catch {
    // Ignore storage errors.
  }
  return [];
}

export function saveBaseNotes(address: string, notes: BaseStoredNote[]): void {
  void notes;
  try {
    localStorage.removeItem(notesKeyPrefix + address.toLowerCase());
  } catch {
    // Ignore storage errors.
  }
}

export async function loadEncryptedBaseNotes(
  address: string,
  cacheSecret: string,
): Promise<BaseStoredNote[]> {
  try {
    const parsed = await loadEncryptedJsonCache<BaseStoredNote[]>(
      "stealthos-base-note-cache-v1",
      encryptedNotesKeyPrefix + address.toLowerCase(),
      address,
      cacheSecret,
      noteJsonReviver,
    );
    return Array.isArray(parsed) ? (parsed as BaseStoredNote[]) : [];
  } catch {
    return [];
  }
}

export async function saveEncryptedBaseNotes(
  address: string,
  notes: BaseStoredNote[],
  cacheSecret: string,
): Promise<void> {
  try {
    await saveEncryptedJsonCache(
      "stealthos-base-note-cache-v1",
      encryptedNotesKeyPrefix + address.toLowerCase(),
      address,
      notes.slice(0, 250),
      cacheSecret,
      noteJsonReplacer,
    );
  } catch {
    // Ignore cache failures; remote inbox remains the source of truth.
  }
}

export function loadBaseHistory(address: string): BaseHistoryEntry[] {
  try {
    localStorage.removeItem(historyKeyPrefix + address.toLowerCase());
  } catch {
    // Ignore storage errors.
  }
  return [];
}

export async function loadEncryptedBaseHistory(
  address: string,
  cacheSecret: string,
): Promise<BaseHistoryEntry[]> {
  try {
    const legacyKey = historyKeyPrefix + address.toLowerCase();
    const encryptedKey = encryptedHistoryKeyPrefix + address.toLowerCase();
    const legacyRaw = localStorage.getItem(legacyKey);
    let legacy: BaseHistoryEntry[] = [];
    if (legacyRaw) {
      legacy = parseHistoryEntries(JSON.parse(legacyRaw));
      localStorage.removeItem(legacyKey);
    }
    const encrypted =
      (await loadEncryptedJsonCache<BaseHistoryEntry[]>(
        "stealthos-base-history-cache-v1",
        encryptedKey,
        address,
        cacheSecret,
      )) ?? [];
    const merged = mergeHistoryEntries(parseHistoryEntries(encrypted), legacy);
    if (legacy.length > 0) {
      await saveEncryptedBaseHistory(address, merged, cacheSecret);
    }
    return merged;
  } catch {
    return [];
  }
}

export function saveBaseHistory(
  address: string,
  history: BaseHistoryEntry[],
): void {
  try {
    localStorage.removeItem(historyKeyPrefix + address.toLowerCase());
  } catch {
    // Ignore storage errors.
  }
}

export async function saveEncryptedBaseHistory(
  address: string,
  history: BaseHistoryEntry[],
  cacheSecret: string,
): Promise<void> {
  try {
    localStorage.removeItem(historyKeyPrefix + address.toLowerCase());
    await saveEncryptedJsonCache(
      "stealthos-base-history-cache-v1",
      encryptedHistoryKeyPrefix + address.toLowerCase(),
      address,
      history.slice(0, 100),
      cacheSecret,
    );
  } catch {
    // Ignore cache failures; private history sync remains the source of truth.
  }
}
